"use client"

import Link from "next/link"
import { motion } from "framer-motion"
import { ArrowLeft, WifiOff } from "lucide-react"

export default function NotFound() {
  return (
    <main className="relative min-h-screen w-full flex flex-col items-center justify-center bg-black overflow-hidden px-6">
      {/* Decorative Blur Glows */}
      <div className="absolute top-1/4 left-1/4 w-[40vw] h-[40vw] bg-emerald-500/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-[30vw] h-[30vw] bg-indigo-500/5 blur-[100px] rounded-full pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center text-center max-w-2xl">
        {/* Label */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
          className="flex items-center gap-3 mb-8"
        >
          <div className="w-8 h-px bg-white/10" />
          <span className="font-mono text-[10px] tracking-[0.5em] text-emerald-500/60 uppercase">
            Error_404 // Not_Found
          </span>
          <div className="w-8 h-px bg-white/10" />
        </motion.div>

        {/* Huge 404 with Background Feel */}
        <motion.h1
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.23, 1, 0.32, 1] }}
          className="font-sans text-[12rem] md:text-[16rem] font-light leading-none tracking-tighter text-white/5 select-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        >
          404
        </motion.h1>

        {/* Main Text */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.23, 1, 0.32, 1] }}
          className="space-y-6 relative"
        >
          <h2 className="font-sans text-5xl md:text-7xl font-light text-white tracking-tight leading-none">
            Page <br />
            <span className="font-playfair italic text-white/40">Not Found</span>
          </h2>
          
          <p className="font-sans text-base md:text-lg text-white/40 font-light max-w-md mx-auto leading-relaxed">
            The page you are looking for doesn't exist or has been moved.
          </p>
        </motion.div>

        {/* Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.23, 1, 0.32, 1] }}
          className="mt-16"
        >
          <Link href="/">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              className="group flex items-center gap-4 px-10 py-5 bg-white text-black rounded-full font-mono text-[10px] tracking-[0.3em] uppercase font-bold transition-all hover:bg-emerald-500 hover:text-white"
            >
              <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
              Return to Home
            </motion.button>
          </Link>
        </motion.div>
      </div>

      {/* Background Interface Elements */}
      <div className="absolute bottom-12 left-12 font-mono text-[8px] tracking-widest text-white/10 uppercase hidden md:block">
        Status: 404 <br />
        Route: Unknown
      </div>
      
      <div className="absolute bottom-12 right-12 flex items-center gap-3 opacity-10 hidden md:flex">
        <WifiOff className="w-4 h-4 text-white" />
        <div className="w-24 h-[1px] bg-white" />
      </div>
    </main>
  )
}
